import type { ViewActionShowIf } from './viewTypes'

// AB#2885 — showIfToExpr (AB#1862) is GONE, and with it the last of the lossy showIf stringifiers.
//
// It normalised the object form to a string, which AB#2774 had already documented as superseded:
// `in` has no string spelling, so `{field, in: [...]}` fell through to a bare truthy test and quietly
// widened a three-way status filter to "status is set". It had no callers left, and AB#2878 deleted
// its tenant-side twin showIfToString for the same reason. Keeping it now would mean a normaliser
// with no spelling for `allOf`/`anyOf` either — silently dropping a conjunction, which is the exact
// class of loss both functions kept causing. There is one evaluator, and it reads the authored shape.

/** A value counts as "set" when it is present and not blank. false is NOT set — an unticked box reads as absent. */
function isSet(value: unknown): boolean {
    return value !== null && value !== undefined && value !== '' && value !== false
}

/**
 * AB#2774 — evaluates a `showIf` in EITHER authored form.
 *
 * The two forms had drifted into two contracts with two evaluators. `ViewAction.showIf` is typed as
 * `string | ViewActionShowIf` and both ADR-0020 and ADR-0025 document the OBJECT form, but the tenant's
 * ActionButton passed the value straight to a string-only evaluator that opens with `expr.trim()`. An
 * object has no `.trim`, so authoring the documented shape threw a TypeError and took the whole action
 * bar down with it — which platform-spike's spike-order view did three times (module since retired,
 * AB#2857).
 *
 * Accepting both forms here (rather than picking a winner and breaking the other) is what stops the two
 * drifting again: there is one evaluator, and the type it accepts is the type the view model declares.
 */
export function evaluateShowIf(
    showIf: string | ViewActionShowIf | undefined,
    data: Record<string, unknown>,
    permissions?: ReadonlySet<string>,
): boolean {
    if (showIf === undefined || showIf === null) { return true }

    // ── object form: evaluated structurally, so `in` survives ──────────────────
    if (typeof showIf !== 'string') {
        // AB#2885 — conjunction/disjunction, checked BEFORE the field forms so a combining node needs
        // no field of its own. Recursive, so branches may nest and may themselves be expression
        // strings (including `permission:CODE`, which is why permissions are threaded down).
        //
        // An EMPTY allOf is true and an empty anyOf is false — the standard identities. That is not
        // pedantry: an author generating conditions from a list would otherwise find an empty list
        // silently hiding the control, and "no restrictions" reading as "never show" is the worst
        // possible default for a visibility rule.
        if (showIf.allOf !== undefined) {
            return showIf.allOf.every(c => evaluateShowIf(c, data, permissions))
        }
        if (showIf.anyOf !== undefined) {
            return showIf.anyOf.some(c => evaluateShowIf(c, data, permissions))
        }

        // A node with neither a combiner nor a field says nothing; treat it as no condition rather
        // than reading `data[undefined]`, which would coerce to a lookup on the literal "undefined".
        if (showIf.field === undefined) { return true }

        const value = data[showIf.field]
        if (showIf.isSet !== undefined) { return isSet(value) === showIf.isSet }
        if (showIf.eq !== undefined) { return String(value ?? '') === String(showIf.eq) }
        if (showIf.neq !== undefined) { return String(value ?? '') !== String(showIf.neq) }
        if (showIf.in !== undefined) { return showIf.in.map(String).includes(String(value ?? '')) }
        return isSet(value)
    }

    const trimmed = showIf.trim()

    // permission:CODE — check user has the named permission
    if (trimmed.startsWith('permission:')) {
        const code = trimmed.slice('permission:'.length).trim()
        return permissions?.has(code) ?? false
    }

    // AB#2774 — !field: the negation of the truthy form, i.e. "this field is unset". "Hide this action
    // once the work is done" is the single most common thing an action wants to say, and the grammar
    // could not say it: there was no negation, and `field=` did not match the equality regex (which
    // demands at least one character after the =), so it fell through to a truthy lookup on the literal
    // key "field=" and was ALWAYS false — the action was permanently hidden rather than conditionally
    // shown, which looks like a permission problem and is not.
    if (trimmed.startsWith('!')) {
        return !isSet(data[trimmed.slice(1).trim()])
    }

    // field!=value, and field!= for "is set" (any non-empty value differs from empty).
    // AB#3107 — [\w.]+ rather than \w+: a DOTTED name is an exposed referenced-record value
    // (`transactionTypeId.movementDirection`), folded into record data as a flat key, so the
    // one-hop walk needs no path parser here — the key either exists or it does not.
    const neq = trimmed.match(/^([\w.]+)!=(.*)$/)
    if (neq?.[1] !== undefined) {
        return String(data[neq[1]] ?? '') !== (neq[2] ?? '')
    }

    // field=value, and field= for "is empty" (dotted names per AB#3107, as above)
    const eq = trimmed.match(/^([\w.]+)=(.*)$/)
    if (eq?.[1] !== undefined) {
        return String(data[eq[1]] ?? '') === (eq[2] ?? '')
    }

    // truthy field
    return isSet(data[trimmed])
}
